import { useMemo } from "react";
import { useMatchGameGraphs } from "./hooks/useMatchGameGraphs";
import { IGameRecord, IPlayer, IPlayerGameStats } from "../../types/types";
import { formatCompactNumber, formatPct } from "../../utils/matchGameSides";
import { PlayerGraphRow } from "../../utils/matchGameGraphData";

interface MatchGameGraphsProps {
  game: IGameRecord;
  gameIndex: number;
  rosterByPuuid: Map<string, IPlayer>;
  playerStats: IPlayerGameStats[];
}

type PlayerStatKey = keyof Pick<
  PlayerGraphRow,
  "damage" | "gold" | "cs" | "vision" | "dpm" | "kpPct"
>;

interface PlayerStatChartProps {
  title: string;
  rows: PlayerGraphRow[];
  dataKey: PlayerStatKey;
  formatValue?: (value: number) => string;
  championAlt: (champion: string) => string;
}

const PlayerStatChart = ({
  title,
  rows,
  dataKey,
  formatValue = formatCompactNumber,
  championAlt,
}: PlayerStatChartProps) => {
  const maxValue = useMemo(() => Math.max(...rows.map((row) => row[dataKey]), 1), [rows, dataKey]);

  return (
    <section
      className="rounded-[var(--surface-card-radius)] border border-[var(--surface-card-border)] bg-black/15 p-[0.85rem]"
      aria-labelledby={`graph-${dataKey}`}
    >
      <h3
        id={`graph-${dataKey}`}
        className="m-0 mb-[0.65rem] font-shentox text-[0.75rem] font-semibold tracking-[0.06em] uppercase opacity-80"
      >
        {title}
      </h3>
      <ul className="m-0 flex flex-col gap-[0.45rem] p-0">
        {rows.map((row) => {
          const value = row[dataKey];
          const widthPct = Math.max((value / maxValue) * 100, value > 0 ? 2 : 0);

          return (
            <li
              key={row.id}
              className="grid list-none grid-cols-[1.75rem_minmax(3.5rem,5.5rem)_minmax(0,1fr)_auto] items-center gap-[0.45rem] max-md:grid-cols-[1.75rem_minmax(0,1fr)_auto] max-md:gap-[0.25rem_0.45rem]"
            >
              <img
                className="h-7 w-7 rounded-[0.3rem] object-cover max-md:col-start-1 max-md:row-span-2 max-md:row-start-1"
                src={row.championIcon}
                alt={championAlt(row.championName)}
                loading="lazy"
              />
              <span
                className="min-w-0 overflow-hidden font-jetbrains text-[0.72rem] text-ellipsis whitespace-nowrap max-md:col-start-2 max-md:row-start-1"
                title={row.name}
              >
                {row.name}
              </span>
              <div
                className="h-[0.65rem] overflow-hidden rounded-full bg-white/[0.08] max-md:col-span-2 max-md:col-start-2 max-md:row-start-2"
                aria-hidden="true"
              >
                <div
                  className="h-full min-w-0 rounded-full transition-[width] duration-[250ms] ease-in-out"
                  style={{ width: `${String(widthPct)}%`, backgroundColor: row.teamColor }}
                />
              </div>
              <span className="min-w-10 text-right font-jetbrains text-[0.72rem] opacity-90 max-md:col-start-3 max-md:row-start-1">
                {formatValue(value)}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export const MatchGameGraphs = ({
  game,
  gameIndex,
  rosterByPuuid,
  playerStats,
}: MatchGameGraphsProps) => {
  const { copy, allPlayers, gameTitle, duration, championAlt } = useMatchGameGraphs({
    game,
    gameIndex,
    rosterByPuuid,
    playerStats,
  });

  return (
    <article className="flex flex-col gap-4 p-4 max-md:p-3">
      <header className="text-center">
        <h2 className="m-0 font-shentox text-[clamp(1rem,1.6vw,1.2rem)] tracking-[0.06em] uppercase">
          {copy.GRAPHS_TITLE}
        </h2>
        <p className="m-0 mt-[0.35rem] flex flex-wrap justify-center gap-[0.45rem] font-jetbrains text-[0.85rem] opacity-80">
          <span>{gameTitle}</span>
          <span aria-hidden="true">·</span>
          <span>{duration}</span>
        </p>
      </header>

      <div className="grid grid-cols-2 gap-4 max-md:grid-cols-1">
        <PlayerStatChart
          title={copy.GRAPHS_DAMAGE}
          rows={allPlayers}
          dataKey="damage"
          championAlt={championAlt}
        />
        <PlayerStatChart
          title={copy.GRAPHS_GOLD}
          rows={allPlayers}
          dataKey="gold"
          championAlt={championAlt}
        />
        <PlayerStatChart
          title={copy.GRAPHS_CS}
          rows={allPlayers}
          dataKey="cs"
          championAlt={championAlt}
        />
        <PlayerStatChart
          title={copy.GRAPHS_VISION}
          rows={allPlayers}
          dataKey="vision"
          championAlt={championAlt}
        />
        <PlayerStatChart
          title={copy.GRAPHS_DPM}
          rows={allPlayers}
          dataKey="dpm"
          formatValue={(value) => String(Math.round(value))}
          championAlt={championAlt}
        />
        <PlayerStatChart
          title={copy.GRAPHS_KP}
          rows={allPlayers}
          dataKey="kpPct"
          formatValue={formatPct}
          championAlt={championAlt}
        />
      </div>
    </article>
  );
};
